import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  FiTag,
  FiCalendar,
  FiArrowRight,
  FiZap,
  FiActivity,
  FiPlus,
  FiUser,
  FiBookOpen,
  FiEdit3,
} from "react-icons/fi";
import Navbar from "../../../common/components/HeaderOpsPage/Navbar";
import Body from "../../../common/components/Body/Body";
import CommentThread from "./CommentThread";
import ProgressDashboardModal from "./ProgressDashboardModal";
import RequestFeatureModal from "./RequestFeatureModal";
import ReleaseEditorModal from "./ReleaseEditorModal";
import {
  getReleaseNotes,
  getReleaseComments,
  getShareAudiences,
  createFeatureRequest,
  addReleaseComment,
  updateRoadmapItem,
  getPendingRequests,
  reviewFeatureRequest,
  getReleaseDrafts,
  saveReleaseDraft,
  submitReleaseDraft,
  reviewReleaseDraft,
} from "../../services/releaseNotesAPI";
import "./ReleaseNotes.css";

// Mirrors STAGES in the release-notes backend — three, deliberately. Anything
// unmapped falls back to the neutral chip rather than rendering with the wrong
// colour.
const STATUS_META = {
  Exploring: { cls: "is-exploring", label: "Exploring" },
  "In Progress": { cls: "is-progress", label: "In Progress" },
  Delivery: { cls: "is-delivery", label: "Delivery" },
};

/**
 * The "how do I actually use this" note attached to a release, plus who wrote
 * and approved it.
 *
 * Release entries are authored and signed off, not generated from finished
 * backlog items — showing the attribution is what makes that visible rather
 * than something you have to be told.
 */
const ReleaseGuidance = ({ release }) => {
  if (!release?.guidance && !release?.authoredBy) return null;
  return (
    <>
      {release.guidance ? (
        <div className="rn-guidance">
          <div className="rn-guidance-label">
            <FiBookOpen size={13} /> How to use it
          </div>
          <p>{release.guidance}</p>
        </div>
      ) : null}
      {release.authoredBy ? (
        <div className="rn-attribution">
          Written by {release.authoredBy}
          {release.approvedBy ? <> · approved by {release.approvedBy}</> : null}
        </div>
      ) : null}
    </>
  );
};

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const ReleaseNotes = () => {
  const [releases, setReleases] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [comments, setComments] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  // Named owner: requests go to them, they approve, they post status updates.
  const [owner, setOwner] = useState(null);
  // Release notes not yet published, and who may act on them.
  const [drafts, setDrafts] = useState([]);
  const [canAuthorReleases, setCanAuthorReleases] = useState(false);
  const [canApproveReleases, setCanApproveReleases] = useState(false);
  // Set by the backend from the caller's JWT. Only decides whether the edit
  // controls render — every write is re-authorised server-side.
  const [isAdmin, setIsAdmin] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ?review=v1.7.0 arrives from the "Review and publish" button in the
  // approval email. It opens the editor straight onto that note so the
  // approver doesn't have to hunt for it in the queue.
  //
  // ?item=<id> arrives from "View the discussion" in a comment notification
  // and opens that item's thread instead.
  const [searchParams, setSearchParams] = useSearchParams();
  const reviewCode = searchParams.get("review") || "";
  const focusItemId = searchParams.get("item") || "";

  const userData = useSelector((state) => state.login?.login?.data?.data?.[0]);
  const currentUser = useMemo(
    () => ({
      name: userData?.FirstName || "User",
      role: userData?.Role || "",
    }),
    [userData],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [notes, existingComments, shareAudiences, pending, noteDrafts] =
          await Promise.all([
            getReleaseNotes(),
            getReleaseComments(),
            getShareAudiences(),
            getPendingRequests(),
            getReleaseDrafts(),
          ]);
        if (cancelled) return;
        setReleases(notes.releases || []);
        setUpcoming(notes.upcoming || []);
        setComments(existingComments || []);
        setAudiences(shareAudiences || []);
        setPendingRequests(pending || []);
        setIsAdmin(notes.isAdmin === true);
        setOwner(notes.owner || null);
        setDrafts(noteDrafts || []);
        setCanAuthorReleases(notes.canAuthorReleases === true);
        setCanApproveReleases(notes.canApproveReleases === true);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load updates");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Notes this user can actually act on — badged on the button so an approver
  // doesn't have to open the modal to discover something is waiting.
  const pendingApprovals = useMemo(
    () => drafts.filter((d) => d.canApprove).length,
    [drafts],
  );

  // Open the editor once the drafts have loaded, so the linked note is already
  // on screen rather than appearing a moment later. Only for people who can
  // actually see drafts — for anyone else the parameter is inert and the page
  // just renders normally.
  useEffect(() => {
    if (!reviewCode || loading) return;
    if (!canAuthorReleases && !canApproveReleases) return;
    setShowEditor(true);
  }, [reviewCode, loading, canAuthorReleases, canApproveReleases]);

  // Drop the parameter once it has been acted on, so a refresh or a shared URL
  // doesn't keep reopening the dialog.
  const closeEditor = () => {
    setShowEditor(false);
    if (reviewCode) {
      searchParams.delete("review");
      setSearchParams(searchParams, { replace: true });
    }
  };

  // Group comments by the item they belong to for O(1) lookups per card.
  const commentsByItem = useMemo(() => {
    const map = {};
    for (const c of comments) {
      if (!c || !c.itemId) continue;
      (map[c.itemId] = map[c.itemId] || []).push(c);
    }
    return map;
  }, [comments]);

  const handleAddComment = async (comment) => {
    const saved = await addReleaseComment(comment);
    // Optimistically merge the persisted comment into local state.
    setComments((prev) => [...prev, saved]);
    return saved;
  };

  const handleCreateRequest = async (request) => {
    // Deliberately NOT added to `upcoming`. A request goes through review
    // before it becomes roadmap work, and showing it in "What's coming up"
    // the moment it was submitted read as a commitment to build it.
    return await createFeatureRequest(request);
  };

  // ── Roadmap admin actions (server re-checks permission on each call) ──

  const handleUpdateItem = async (patch) => {
    const saved = await updateRoadmapItem(patch);
    setUpcoming((prev) =>
      prev.map((it) => (it.id === saved.id ? { ...it, ...saved } : it)),
    );
    return saved;
  };

  // Approving moves the request onto the roadmap; declining retires it. Either
  // way it leaves the review queue.
  // ── Release-note authoring ──
  // Every action refetches the drafts list rather than patching it locally:
  // a note's status is the server's to decide (a publish can be refused for
  // self-approval), so mirroring the transition here risks showing a state
  // that didn't happen.
  const refreshDrafts = async () => setDrafts((await getReleaseDrafts()) || []);

  const handleSaveDraft = async (draft) => {
    const saved = await saveReleaseDraft(draft);
    await refreshDrafts();
    return saved;
  };

  const handleSubmitDraft = async (code) => {
    const saved = await submitReleaseDraft(code);
    await refreshDrafts();
    return saved;
  };

  const handleReviewDraft = async (decision) => {
    const saved = await reviewReleaseDraft(decision);
    await refreshDrafts();
    // Publishing puts a note on the page, so the public list has to catch up.
    if (decision.approve) {
      const fresh = await getReleaseNotes();
      setReleases(fresh.releases || []);
    }
    return saved;
  };

  const handleReviewRequest = async ({ itemId, approve }) => {
    const saved = await reviewFeatureRequest({ itemId, approve });
    setPendingRequests((prev) => prev.filter((r) => r.id !== itemId));
    if (approve) {
      const fresh = await getReleaseNotes();
      setUpcoming(fresh.upcoming || []);
    }
    return saved;
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <Body>
          <div className="rn-state">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
            <p>Loading product updates…</p>
          </div>
        </Body>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Navbar />
        <Body>
          <div className="rn-state">
            <FiZap size={44} color="#ef4444" />
            <h2>Couldn't load updates</h2>
            <p>{error}</p>
          </div>
        </Body>
      </div>
    );
  }

  const latest = releases[0];
  const previous = releases.slice(1);

  return (
    <div>
      <Navbar />
      <Body>
        <div className="rn-page">
          <div className="rn-header">
            <div className="rn-header-text">
              <h1>What's New &amp; What's Next</h1>
              <p className="rn-subtitle">
                A running log of what we've shipped and what's coming up. Have a
                thought or want to redirect a priority? Drop a comment on any
                item.
              </p>
            </div>
            {/* Two distinct actions: raising a request is its own thing, not a
                sub-section of the roadmap dashboard. */}
            <div className="rn-header-actions">
              {/* Only for the people who write or sign off notes. */}
              {(canAuthorReleases || canApproveReleases) && (
                <button
                  type="button"
                  className="rn-request-btn"
                  onClick={() => setShowEditor(true)}
                >
                  <FiEdit3 size={16} /> Release Notes
                  {pendingApprovals > 0 ? (
                    <span className="rn-badge-count">{pendingApprovals}</span>
                  ) : null}
                </button>
              )}
              <button
                type="button"
                className="rn-dashboard-btn"
                onClick={() => setShowDashboard(true)}
              >
                <FiActivity size={16} /> Roadmap Progress
              </button>
              <button
                type="button"
                className="rn-request-btn"
                onClick={() => setShowRequest(true)}
              >
                <FiPlus size={16} /> New Request
              </button>
            </div>
          </div>

          {/* Who owns this, stated on the page rather than left to be guessed:
              where a request goes, who approves it, and who posts the status
              updates are all the same person. */}
          {owner?.name ? (
            <div className="rn-ownership">
              <FiUser size={15} />
              <p>
                Requests go to <strong>{owner.name}</strong>, who reviews them,
                decides what joins the roadmap, and keeps the stages current.
                {owner.email ? (
                  <>
                    {" "}
                    <a href={`mailto:${owner.email}`}>{owner.email}</a>
                  </>
                ) : null}{" "}
                Release notes are written and approved before publishing — an
                item finishing on the roadmap doesn't publish itself.
              </p>
            </div>
          ) : null}

          <div className="rn-layout">
            {/* ─── Releases column ─────────────────────────────── */}
            <section className="rn-col rn-col-main">
              <h2 className="rn-col-title">Recent releases</h2>

              {latest && (
                <article className="rn-release rn-release-latest">
                  <div className="rn-release-head">
                    <span className="rn-badge rn-badge-latest">Latest</span>
                    <span className="rn-release-id">
                      <FiTag size={13} /> {latest.id}
                    </span>
                    <span className="rn-release-date">
                      <FiCalendar size={13} /> {formatDate(latest.date)}
                    </span>
                  </div>
                  <h3 className="rn-release-title">{latest.title}</h3>
                  {latest.tags?.length ? (
                    <div className="rn-tags">
                      {latest.tags.map((t) => (
                        <span key={t} className="rn-chip">
                          {t}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <div className="rn-whatsnew">
                    <div className="rn-whatsnew-label">What's New</div>
                    <ul className="rn-highlights">
                      {latest.highlights.map((h, i) => (
                        <li key={i}>{h}</li>
                      ))}
                    </ul>
                  </div>
                  <ReleaseGuidance release={latest} />
                  <CommentThread
                    itemId={latest.id}
                    comments={commentsByItem[latest.id] || []}
                    currentUser={currentUser}
                    audiences={audiences}
                    onAdd={handleAddComment}
                    isLinked={focusItemId === latest.id}
                  />
                </article>
              )}

              {previous.map((r) => (
                <article key={r.id} className="rn-release">
                  <div className="rn-release-head">
                    <span className="rn-release-id">
                      <FiTag size={13} /> {r.id}
                    </span>
                    <span className="rn-release-date">
                      <FiCalendar size={13} /> {formatDate(r.date)}
                    </span>
                  </div>
                  <h3 className="rn-release-title">{r.title}</h3>
                  {r.tags?.length ? (
                    <div className="rn-tags">
                      {r.tags.map((t) => (
                        <span key={t} className="rn-chip">
                          {t}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <div className="rn-whatsnew">
                    <div className="rn-whatsnew-label">What's New</div>
                    <ul className="rn-highlights">
                      {r.highlights.map((h, i) => (
                        <li key={i}>{h}</li>
                      ))}
                    </ul>
                  </div>
                  <ReleaseGuidance release={r} />
                  <CommentThread
                    itemId={r.id}
                    comments={commentsByItem[r.id] || []}
                    currentUser={currentUser}
                    audiences={audiences}
                    onAdd={handleAddComment}
                    isLinked={focusItemId === r.id}
                  />
                </article>
              ))}

              {releases.length === 0 && (
                <div className="rn-empty">No releases to show yet.</div>
              )}
            </section>

            {/* ─── Upcoming column ─────────────────────────────── */}
            <aside className="rn-col rn-col-side">
              <h2 className="rn-col-title">
                <FiArrowRight size={16} /> Upcoming
              </h2>
              <p className="rn-col-hint">
                The next few items on our backlog. Comment to weigh in on
                priorities.
              </p>

              {upcoming.map((item) => {
                const meta = STATUS_META[item.status] || {
                  cls: "is-exploring",
                  label: item.status,
                };
                return (
                  <article key={item.id} className="rn-upcoming">
                    <div className="rn-upcoming-head">
                      <span className={`rn-status ${meta.cls}`}>
                        {meta.label}
                      </span>
                      {item.eta ? (
                        <span className="rn-eta">{item.eta}</span>
                      ) : null}
                    </div>
                    <h4 className="rn-upcoming-title">{item.title}</h4>
                    <p className="rn-upcoming-desc">{item.description}</p>
                    <CommentThread
                      itemId={item.id}
                      comments={commentsByItem[item.id] || []}
                      currentUser={currentUser}
                      audiences={audiences}
                      onAdd={handleAddComment}
                      isLinked={focusItemId === item.id}
                    />
                  </article>
                );
              })}

              {upcoming.length === 0 && (
                <div className="rn-empty">Nothing queued right now.</div>
              )}
            </aside>
          </div>
        </div>

        <ProgressDashboardModal
          open={showDashboard}
          onClose={() => setShowDashboard(false)}
          items={upcoming}
          pendingRequests={pendingRequests}
          isAdmin={isAdmin}
          owner={owner}
          currentUser={currentUser}
          onUpdateItem={handleUpdateItem}
          onReviewRequest={handleReviewRequest}
        />

        <RequestFeatureModal
          open={showRequest}
          onClose={() => setShowRequest(false)}
          onCreate={handleCreateRequest}
          owner={owner}
        />

        <ReleaseEditorModal
          open={showEditor}
          onClose={closeEditor}
          drafts={drafts}
          canAuthor={canAuthorReleases}
          canApprove={canApproveReleases}
          currentUser={currentUser}
          focusCode={reviewCode}
          onSave={handleSaveDraft}
          onSubmit={handleSubmitDraft}
          onReview={handleReviewDraft}
        />
      </Body>
    </div>
  );
};

export default ReleaseNotes;
